import React, {useContext, useRef, useState} from 'react';
import {Button, Card, Col, Flex, Row, Skeleton, message} from "antd";
import LeftUpload, {LeftUploadHandle} from "./LeftUpload"
import RightUpload, {RightUploadHandle} from "./RightUpload";
import ChapterCompare from "./ChaptersCompare";
import {StatusContext} from "../../../services/types";
import {fetchExampleFile} from "../../../services/onboarding/fetchExampleFile";
import {EXAMPLE_ONBOARDING} from "../../../services/onboarding/config";
import {applyChapterPreset} from "../../../services/onboarding/chapterPreset";

enum LocalStep {Left = 0, Right, RightReady}

interface LocalStatus {
    step: LocalStep;
}

const StepStructure: React.FC = () => {
    const statusContext = useContext(StatusContext);
    if (!statusContext) {
        throw new Error("missing context");
    }

    const { status, setStatus } = statusContext;

    const leftUploadRef = useRef<LeftUploadHandle | null>(null);
    const rightUploadRef = useRef<RightUploadHandle | null>(null);

    const setResult = (success: boolean, data: any, error: any) : void => {
        if (!success) {
            console.log(error);
            return;
        }

        setLocalStatus(prevLocal => {
            const nextStep = prevLocal.step + 1;
            setStatus(prevStatus => ({
                ...prevStatus,
                ...data,
                nextEnabled: nextStep >= LocalStep.RightReady,
            }));
            return {
                step: nextStep,
            } as LocalStatus;
        });
    };

    // content comparison status
    const [localStatus, setLocalStatus] = useState<LocalStatus>({
        step: LocalStep.Left,
    });

    const [isExampleLoading, setIsExampleLoading] = useState(false);

    const startExampleOnboarding = async () => {
        if (isExampleLoading) return;
        setIsExampleLoading(true);
        try {
            const leftHandle = leftUploadRef.current;
            if (!leftHandle) {
                throw new Error('Left uploader is unavailable');
            }

            const leftFile = await fetchExampleFile(EXAMPLE_ONBOARDING.left);
            const leftResult = await leftHandle.uploadExampleFile(leftFile);

            const rightHandle = rightUploadRef.current;
            if (!rightHandle) {
                throw new Error('Right uploader is unavailable');
            }
            const rightFile = await fetchExampleFile(EXAMPLE_ONBOARDING.right);
            await rightHandle.uploadExampleFile(rightFile, leftResult.project_id);

            setStatus(prev => {
                const presetResult = applyChapterPreset(EXAMPLE_ONBOARDING.chapterPreset, prev.leftChapters, prev.rightChapters);
                return {
                    ...prev,
                    leftChapters: presetResult.leftChapters ?? prev.leftChapters,
                    rightChapters: presetResult.rightChapters ?? prev.rightChapters,
                };
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load the example books. Please try again.');
        } finally {
            setIsExampleLoading(false);
        }
    };

    const canShowExampleButton = localStatus.step === LocalStep.Left;
    const exampleButtonDisabled = isExampleLoading;

    return (
        <>
            <Row gutter={16}>
                <Col xs={24} xl={12}>
                    <Card bordered={false}>
                        <LeftUpload ref={leftUploadRef} setResult={setResult} />
                        {
                            canShowExampleButton ? (
                                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        type="default"
                                        onClick={startExampleOnboarding}
                                        loading={isExampleLoading}
                                        disabled={exampleButtonDisabled}
                                    >
                                        Or proceed with the example book
                                    </Button>
                                </div>
                            ) : null
                        }
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card bordered={false}>
                        <Flex justify="center" align="center" style={{ height: '100%' }}>
                            <div style={{ width: '100%', display: localStatus.step > LocalStep.Left ? 'block' : 'none' }}>
                                <RightUpload ref={rightUploadRef} setResult={setResult} />
                            </div>
                            {
                                localStatus.step === LocalStep.Left ?
                                    <Skeleton /> : null
                            }
                        </Flex>
                    </Card>
                </Col>
            </Row>
            <Row style={{ marginTop: 24 }} gutter={16}>
            {
                localStatus.step > LocalStep.Right ? <ChapterCompare/> : null
            }
            </Row>
        </>
    )
}

export default StepStructure;